import { useMemo, useCallback, useRef } from 'react';
import type { WrappedComponentProps } from 'react-intl';

import type { AnnotationTypes } from '@atlaskit/adf-schema/annotation';
import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { AnnotationManager } from '@atlaskit/editor-common/annotation';
import type { EditorActions } from '@atlaskit/editor-core/actions';
import type { ChromelessEditor } from '@atlaskit/editor-core/appearance-editor-chromeless';
import type { CommentEditor } from '@atlaskit/editor-core/appearance-editor-comment';
import type {
	InlineCommentViewComponentProps,
	InlineCommentCreateComponentProps,
	AnnotationState,
	InlineCommentState,
} from '@atlaskit/editor-plugin-annotation/types';

import { getEditorInlineCommentOptions } from '@confluence/annotation-provider-inline-comments/entry-points/editor.ts';
import {
	useAnnotationsDispatch,
	useAnnotationManager,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { fg } from '@confluence/feature-gating';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks/entry-points/useAddCommentPermissionCheck.ts';
import { useContentType } from '@confluence/page-context';
import { useActiveInlineData } from '@confluence/experiment-inline-reactions/entry-points/useActiveInlineData.ts';
import { useInlineReactionMarkers } from '@confluence/experiment-inline-reactions/entry-points/useInlineReactionMarkers.ts';
import type { CreateInlineCommentHighlight } from '@confluence/comment-context/entry-points/InlineCommentsContext.ts';
import type {
	EditorCommentMode,
	EditorCommentType,
} from '@confluence/comment/entry-points/commentTypes.ts';
import type { CommentActionType } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import type { LoadableType } from '@confluence/loadable';

type OnContentChangeWithDataHandler = (hasContentChange: boolean, content?: DocNode) => void;
type EditorAppearance = 'chromeless' | 'comment';
type AfterEditorActivateCallback = (editorRef: HTMLElement | null) => void;
type InlineCommentsMode = 'view-all' | 'view' | 'edit';

export const useEditorAnnotationProviderOptions = ({
	pageId,
	editorActions,
	triggerAnalyticsEvent,
	updateUnresolvedInlineComment,
}: {
	pageId: string;
	editorActions: any;
	triggerAnalyticsEvent: any;
	updateUnresolvedInlineComment: any;
}): {
	inlineComment: {
		viewComponent: React.MemoExoticComponent<
			(viewComponentProps: InlineCommentViewComponentProps) => React.JSX.Element
		>;
		createComponent: React.MemoExoticComponent<
			(createComponentProps: InlineCommentCreateComponentProps) => React.JSX.Element
		>;
		getState: (annotations: any) => Promise<AnnotationState<AnnotationTypes, InlineCommentState>[]>;
		updateSubscriber: any;
		disallowOnWhitespace: boolean;
		isToolbarAbove: boolean;
		supportedBlockNodes: string[];
		onCommentButtonMount: () => ReturnType<
			LoadableType<
				Omit<
					Omit<
						WrappedComponentProps & {
							newCommentHighlight?: CreateInlineCommentHighlight;
							pageId: string;
							pageType: string;
							appearance: EditorAppearance;
							commentId?: string;
							commentMode: EditorCommentMode;
							commentType: EditorCommentType;
							spaceId: string;
							onSaveComment?: (adf: any, onSuccess: () => void) => Promise<any>;
							content?: DocNode;
							useNewWarningModal?: boolean;
							showCancelButton?: boolean;
							onCancelComment?: (params?: { destroy?: boolean }) => void;
							onContentChange?: (hasContentChange: boolean) => void;
							onContentChangeWithData?: OnContentChangeWithDataHandler;
							onEditorReady?: (editorActions: EditorActions) => void;
							afterActivation?: AfterEditorActivateCallback;
							hasMediaUploadPermissions: boolean;
							shouldResetEditor?: boolean;
							onEditorReset?: () => void;
							expandEditor?: boolean;
							shouldWarnOnInternalNavigation?: boolean;
							hideWatchCheckbox?: boolean;
							pageMode?: InlineCommentsMode;
							flags: FlagsStateContainer;
							setShouldRefetch?: () => void;
							EditorComponent: typeof CommentEditor | typeof ChromelessEditor;
							commentContainerElementRef?: React.MutableRefObject<HTMLElement | null>;
							isCommentsPanel?: boolean;
							isFooterComment?: boolean;
							wasRemovedByAnotherUser?: CommentActionType | boolean;
							wasDraftAnnotationRemoved?: boolean;
							isStandaloneComment?: boolean;
							isOffline?: boolean;
							customAriaLabel?: string;
							popupsMountPoint?: HTMLElement;
							popupsScrollableElement?: HTMLElement;
							hasParentMaxHeight?: boolean;
						} & {
							topLevelCommentId?: string;
						},
						'intl'
					> & {
						forwardedRef?: React.Ref<any>;
					},
					'flags'
				>
			>['preload']
		>;
		getCanAddComments: () => boolean;
		contentType: string | undefined;
	};
	annotationManager: AnnotationManager | undefined;
} => {
	const { setEditorCreateProps, setEditorViewProps } = useAnnotationsDispatch();
	const annotationManager = useAnnotationManager();
	const [contentType] = useContentType();

	const { canAddComments } = useAddCommentPermissionCheck(pageId);

	const canAddCommentsRef = useRef(canAddComments);
	canAddCommentsRef.current = canAddComments;

	const getCanAddComments = useCallback(() => canAddCommentsRef.current, []);

	const { reactionMarkerRefs, reactionMarkerMap } = useActiveInlineData(pageId);
	const { mergedReactionMarkerMap, optimisticResolvedMarkerRefs } = useInlineReactionMarkers({
		reactionMarkerRefs,
		reactionMarkerMap,
	});

	const mergedReactionMarkerMapRef = useRef(mergedReactionMarkerMap);
	mergedReactionMarkerMapRef.current = mergedReactionMarkerMap;
	const getReactionMarkerMap = useCallback(() => mergedReactionMarkerMapRef.current, []);
	const optimisticResolvedMarkerRefsRef = useRef(optimisticResolvedMarkerRefs);
	optimisticResolvedMarkerRefsRef.current = optimisticResolvedMarkerRefs;
	const getOptimisticResolvedMarkerRefs = useCallback(
		() => optimisticResolvedMarkerRefsRef.current,
		[],
	);

	return useMemo(
		() => ({
			inlineComment: getEditorInlineCommentOptions({
				pageId,
				editorActions,
				triggerAnalyticsEvent,
				updateUnresolvedInlineComment,
				setEditorCreateProps,
				setEditorViewProps,
				editorAnnotationProviderManagersEnabled: fg(
					'confluence_frontend_editor_annotation_manager',
				),
				getCanAddComments,
				contentType,
				getReactionMarkerMap,
				getOptimisticResolvedMarkerRefs,
			}),
			annotationManager,
		}),
		[
			pageId,
			triggerAnalyticsEvent,
			editorActions,
			updateUnresolvedInlineComment,
			setEditorCreateProps,
			setEditorViewProps,
			annotationManager,
			contentType,
			getCanAddComments,
			getReactionMarkerMap,
			getOptimisticResolvedMarkerRefs,
		],
	);
};
